import { useState } from "react";
import { Linkedin, Link2, Check, Share2, Twitter, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ArticleShareBarProps {
  path: string;
  title: string;
  description?: string;
}

/**
 * Share controls rendered at the top/bottom of every article page.
 * LinkedIn + X/Twitter + Email + Copy + Native Share (mobile).
 */
export const ArticleShareBar = ({ path, title, description }: ArticleShareBarProps) => {
  const [copied, setCopied] = useState(false);

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://davidisei.com";
  const fullUrl = `${origin}${path}`;

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(fullUrl)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description ?? ""}\n\n${fullUrl}`)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast({ title: "Link copied", description: fullUrl });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Could not copy link", description: fullUrl, variant: "destructive" });
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: description, url: fullUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  const baseBtn =
    "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:border-accent/60 hover:bg-background transition-colors";

  return (
    <div role="group" aria-label="Share this article" className="flex flex-wrap items-center gap-2 mb-8">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mr-1">Share:</span>
      <a href={linkedInUrl} target="_blank" rel="noopener noreferrer" aria-label={`Share "${title}" on LinkedIn`} className={baseBtn}>
        <Linkedin className="h-3 w-3" /> LinkedIn
      </a>
      <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label={`Share "${title}" on X`} className={baseBtn}>
        <Twitter className="h-3 w-3" /> X
      </a>
      <a href={mailtoUrl} aria-label="Share by email" className={baseBtn}>
        <Mail className="h-3 w-3" /> Email
      </a>
      <button type="button" onClick={handleCopy} className={baseBtn} aria-label="Copy article link">
        {copied ? <Check className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <button type="button" onClick={handleNativeShare} className={`${baseBtn} sm:hidden`} aria-label="Share via device">
        <Share2 className="h-3 w-3" /> More
      </button>
    </div>
  );
};

export default ArticleShareBar;
